/**
 * Plush infinite scroll for the collection grid.
 *
 * Progressive enhancement over Dawn's numbered pagination:
 *  - When the pagination scrolls into view, the next page is fetched and its
 *    product cards are appended to the current grid.
 *  - The numbered pagination is hidden while auto-loading, and shown again as a
 *    fallback if a fetch fails.
 *  - Re-binds itself ONLY when Dawn's facet filtering replaces the grid node.
 *
 * Enable/disable via the container's data-infinite-scroll="true|false" (set from
 * the theme setting).
 */
(function () {
  'use strict';

  var GRID_ID = 'product-grid';
  var CONTAINER_ID = 'ProductGridContainer';

  // One live loader at a time. `grid` is the exact <ul> node we've bound to, so we
  // can tell our own appended cards (same node) apart from a facet-filter swap
  // (brand-new node) — the latter is the only thing that should re-bind.
  var current = { grid: null, observer: null, token: null };

  function isEnabled() {
    var c = document.getElementById(CONTAINER_ID);
    return !!c && c.getAttribute('data-infinite-scroll') === 'true';
  }

  function getNextUrl(wrapper) {
    if (!wrapper) return null;
    // Dawn quirk: the "next page" link (paginate.next) is rendered with the class
    // `pagination__item--prev`, while the PREVIOUS-page arrow gets `--next`. So we
    // target `--prev` specifically. On the last page there is no next link, so this
    // returns null and auto-loading stops.
    var next = wrapper.querySelector('a.pagination__item--prev');
    return next ? next.getAttribute('href') : null;
  }

  function teardown() {
    if (current.observer) {
      current.observer.disconnect();
      current.observer = null;
    }
    current.token = null;
  }

  function bind() {
    if (!isEnabled()) return;

    var container = document.getElementById(CONTAINER_ID);
    if (!container) return;

    var grid = container.querySelector('ul#' + GRID_ID) || document.querySelector('ul#' + GRID_ID);
    var wrapper = container.querySelector('.pagination-wrapper');
    if (!grid || !wrapper) return;

    // Already bound to this exact grid — do nothing. Prevents the runaway where a
    // second (third, fourth…) loader spins up and re-appends the same pages.
    if (current.grid === grid && current.observer) return;

    teardown();
    current.grid = grid;

    // Identifies this bind. A filter swap calls bind() again, and the request
    // still in flight from the previous grid must not act on the new one.
    var token = {};
    current.token = token;

    function isStale() {
      return current.token !== token;
    }

    var nextUrl = getNextUrl(wrapper);
    if (!nextUrl) return; // no more pages

    var loading = false;
    // Never fetch the same page twice.
    var visited = {};

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !loading) {
            loadNext();
          }
        });
      },
      { rootMargin: '400px 0px' }
    );
    current.observer = observer;
    observer.observe(wrapper);
    wrapper.classList.add('pagination-wrapper--auto');

    function stop() {
      // Only ever tear down our own observer and our own pagination, never the
      // ones a newer bind installed.
      observer.disconnect();
      if (!isStale()) {
        current.observer = null;
        current.token = null;
      }
      if (wrapper && wrapper.parentNode) wrapper.remove();
    }

    function loadNext() {
      if (!nextUrl || visited[nextUrl]) {
        stop();
        return;
      }
      visited[nextUrl] = true;
      loading = true;
      wrapper.setAttribute('aria-busy', 'true');

      fetch(nextUrl)
        .then(function (res) {
          if (!res.ok) throw new Error('Network error');
          return res.text();
        })
        .then(function (html) {
          // The grid we were loading into has been replaced; this page belongs
          // to the filter the shopper has already moved away from.
          if (isStale()) {
            observer.disconnect();
            return;
          }
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector('ul#' + GRID_ID);
          var newWrapper = doc.querySelector('#' + CONTAINER_ID + ' .pagination-wrapper');

          if (newGrid) {
            Array.prototype.forEach.call(newGrid.children, function (item) {
              grid.appendChild(item.cloneNode(true));
            });
          }

          // Advance to the following page, or stop if none / already seen.
          nextUrl = getNextUrl(newWrapper);
          if (nextUrl && !visited[nextUrl]) {
            loading = false;
            wrapper.removeAttribute('aria-busy');
            // Re-arm the observer so it re-checks intersection for the next page.
            observer.unobserve(wrapper);
            observer.observe(wrapper);
          } else {
            stop();
          }
        })
        .catch(function () {
          if (isStale()) {
            observer.disconnect();
            return;
          }
          // Fail open: reveal the real pagination so users can page manually.
          loading = false;
          wrapper.classList.remove('pagination-wrapper--auto');
          wrapper.removeAttribute('aria-busy');
          teardown();
        });
    }
  }

  // Initial bind.
  if (document.readyState !== 'loading') {
    bind();
  } else {
    document.addEventListener('DOMContentLoaded', bind);
  }

  // Re-bind ONLY when facet filtering swaps in a brand-new grid node. Appending our
  // own cards keeps the same <ul>, so those mutations are ignored — that's what
  // stopped the duplicate-loader runaway.
  var root = document.getElementById(CONTAINER_ID);
  if (root && 'MutationObserver' in window) {
    var reinit = new MutationObserver(function () {
      var grid = document.getElementById(GRID_ID);
      if (grid && grid !== current.grid) {
        teardown();
        current.grid = null;
        bind();
      }
    });
    reinit.observe(root, { childList: true, subtree: true });
  }
})();
